'use client';

import { EditorContent, type Extension } from '@tiptap/react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useMemo, useRef } from 'react';
import { MentionContext } from '@/app/components/shared/MentionsTextarea/context';
import { useCustomMention } from '@/app/components/shared/MentionsTextarea/hooks/use-custom-mention';
import { toast } from '@/app/components/ui/use-toast';
import { MAX_PROMPT_LENGTH } from '@/app/constants';
import { useEditorWithExtensions } from '@/app/hooks/editor';
import type { MentionItem, ReferencePrompt, TextPromptWithInputItem } from '@/app/types';
import { cn, extractMentionsIdsFromEditor } from '@/app/utils';

export const QUICK_EDIT_MENTION_COLOR = '#FFFFFF52';

interface QuickEditTextareaProps {
  className?: string;
  textPrompt: TextPromptWithInputItem;
  updateTextPrompt: (value: TextPromptWithInputItem) => void;
  onBeforeDeleteMention?: (mentionsIdsToDelete: string[], updatedContent: TextPromptWithInputItem) => boolean;
  onBeforeAddMention?: (mentionItem: MentionItem) => boolean;
  getExtraItems?: () => ReferencePrompt[];
  getPrefix?: (mentionId: string, label: string) => ReactNode | null;
  popupContainer?: HTMLElement | (() => HTMLElement) | null;
}

export const QuickEditTextarea = ({
  className,
  textPrompt,
  updateTextPrompt,
  onBeforeDeleteMention,
  onBeforeAddMention,
  getExtraItems,
  getPrefix,
  popupContainer,
}: QuickEditTextareaProps) => {
  const t = useTranslations();
  const previousMentionsIds = useRef<string[]>([]);

  // QuickEditDialog 特定配置：禁用远程搜索，z-index 51，禁用 tooltip
  const { mentionExtension, onUpdateCallback } = useCustomMention({
    previousMentionsIds,
    onBeforeAddMention,
    onBeforeDeleteMention,
    getExtraItems,
    enableRemoteSearch: false,
    zIndex: 51,
    popupContainer,
    mentionColor: QUICK_EDIT_MENTION_COLOR,
    disableTooltip: true,
  });

  const { editor, isEmpty } = useEditorWithExtensions({
    content: textPrompt,
    updateContent: updateTextPrompt,
    maxLength: MAX_PROMPT_LENGTH,
    onMaxLengthExceeded: () => {
      toast({
        type: 'warning',
        message: t('create.createForm.error.invalidLength', { limit: MAX_PROMPT_LENGTH }),
      });
    },
    extensions: [mentionExtension as Extension],
    editorProps: {
      attributes: {
        class: 'h-full',
      },
    },
    onUpdateCallback,
  });

  const mentionContextValue = useMemo(() => ({ getPrefix }), [getPrefix]);

  // 不管内部更新（editor变化）还是外部更新（textPrompt变化）都更新 previousMentionsIds
  useEffect(() => {
    if (editor) {
      // textPrompt 变化后，经过 setTimeout 才更新 editor，所以需要 setTimeout 来获取正确的 previousMentionsIds
      setTimeout(() => {
        previousMentionsIds.current = extractMentionsIdsFromEditor(editor);
      }, 0);
    }
  }, [editor, textPrompt]);

  return (
    <div
      className={cn('relative flex w-full flex-col text-sm leading-6 sm:text-sm lg:text-xs lg:leading-5', className)}
    >
      <div className="absolute inset-0 overflow-auto">
        <MentionContext.Provider value={mentionContextValue}>
          <EditorContent
            editor={editor}
            className="h-full w-full rounded-none border-none bg-transparent p-0 text-sm text-system-white leading-6 caret-system-blue02 focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-sm lg:text-xs lg:leading-5"
          />
        </MentionContext.Provider>
      </div>

      {isEmpty && (
        // 显示 placeholder
        <div className="pointer-events-none absolute inset-0 select-none items-center text-system-white36">
          {t('tools.quickEditForm.placeholder')}
        </div>
      )}
    </div>
  );
};
